import PropTypes from 'prop-types';
import { useCallback, useMemo, useState } from 'react';
import FundraisingTableCard from 'features/IRWorkstation/components/Fundraising/FundraisingTableCard';
import LPFundraisingFilters, {
	LP_INITIAL_FILTERS,
} from 'features/IRWorkstation/components/LP360/LPFundraisingFilters';
import useAccountFundraisings from 'features/IRWorkstation/hooks/useAccountFundraisings';
import useFundraisingSort from 'features/IRWorkstation/hooks/useFundraisingSort';
import {
	LP_FUNDRAISING_COLUMNS,
	LP_FUNDRAISING_SORT_KEYS,
} from 'features/IRWorkstation/constants/fundraisingColumns';
import { useIRWorkstation } from 'features/IRWorkstation/contexts/IRWorkstationContext';
import { useTrackEvent } from 'hooks/useTrackEvent';
import { ButtonClickTargets } from 'constants/buttonClickTargets';

const SORT_STORAGE_KEY = 'lpFundraisingOpportunities:sort';

const FILTER_CLICK_TARGETS = {
	active: ButtonClickTargets.LP360FRActiveFilter,
	existing_investor: ButtonClickTargets.LP360FRExistingInvestorFilter,
	stage: ButtonClickTargets.LP360FRStageFilter,
	closing_round: ButtonClickTargets.LP360FRClosingRoundFilter,
	commitment_date_from: ButtonClickTargets.LP360FRFromDateFilter,
	commitment_date_to: ButtonClickTargets.LP360FRToDateFilter,
};

// The LP360 Fundraising Opportunities card: the shared fundraising table card
// scoped to one LP, so every row's Account is the LP being viewed and the lead
// column is the Entity (fund) instead.
const LPFundraisingsCard = ({ accountId }) => {
	const [filters, setFilters] = useState(LP_INITIAL_FILTERS);
	const { tenant } = useIRWorkstation();
	const { trackButtonClick } = useTrackEvent({ tenant, product: 'IEM' });
	const { sort, applySort } = useFundraisingSort(
		LP_FUNDRAISING_SORT_KEYS,
		SORT_STORAGE_KEY
	);
	const {
		fundraisings,
		totalCount,
		loading,
		loadingMore,
		hasMore,
		error,
		loadMore,
		refresh,
	} = useAccountFundraisings(accountId, filters, sort);

	const sortModel = useMemo(
		() => (sort.sortBy ? [{ field: sort.sortBy, sort: sort.sortDir }] : []),
		[sort.sortBy, sort.sortDir]
	);

	const handleFiltersChange = useCallback(
		(newFilters) => {
			Object.entries(FILTER_CLICK_TARGETS).forEach(([key, target]) => {
				if (newFilters[key] !== filters[key]) {
					trackButtonClick(target);
				}
			});

			setFilters(newFilters);
		},
		[filters, trackButtonClick]
	);

	const hasActiveFilters = Object.values(filters).some((v) => v !== '');
	const noRowsMessage = hasActiveFilters
		? 'No records match the selected filters'
		: 'No fundraising history on record';

	return (
		<FundraisingTableCard
			title='Fundraising opportunities'
			totalCount={totalCount}
			cardTestId='lp-fundraisings-card'
			filterBar={
				<LPFundraisingFilters
					filters={filters}
					onChange={handleFiltersChange}
				/>
			}
			columnDefs={LP_FUNDRAISING_COLUMNS}
			rows={fundraisings}
			loading={loading}
			loadingMore={loadingMore}
			hasMore={hasMore}
			error={error}
			sortModel={sortModel}
			onSortModelChange={applySort}
			onRowsScrollEnd={loadMore}
			noRowsMessage={noRowsMessage}
			dataTestId='lp-fundraisings-grid'
			onRefresh={refresh}
		/>
	);
};

LPFundraisingsCard.propTypes = {
	accountId: PropTypes.string.isRequired,
};

export default LPFundraisingsCard;
